import React, { useState } from 'react';
import { MessageSquare, Save, Loader2, CheckCircle, RotateCcw } from 'lucide-react';

interface PromptTemplate {
  key: string;
  label: string;
  description: string;
  prompt: string;
  defaultPrompt: string;
}

const DEFAULT_PROMPTS: PromptTemplate[] = [
  {
    key: 'system_prompt',
    label: 'System Prompt',
    description: 'Hauptanweisung für den KI-Coach. Definiert Verhalten und Persönlichkeit.',
    prompt: `Du bist ein freundlicher und geduldiger Fahrschul-Lernassistent. Dein Name ist Fahryo Coach.

Regeln:
- Erkläre Verkehrsregeln einfach und verständlich
- Nutze kurze Sätze und konkrete Beispiele
- Wenn der Nutzer eine falsche Antwort gibt, erkläre warum sie falsch ist
- Motiviere den Nutzer und lobe Fortschritte
- Antworte immer auf Deutsch
- Halte Antworten unter 200 Wörtern`,
    defaultPrompt: `Du bist ein freundlicher und geduldiger Fahrschul-Lernassistent. Dein Name ist Fahryo Coach.

Regeln:
- Erkläre Verkehrsregeln einfach und verständlich
- Nutze kurze Sätze und konkrete Beispiele
- Wenn der Nutzer eine falsche Antwort gibt, erkläre warum sie falsch ist
- Motiviere den Nutzer und lobe Fortschritte
- Antworte immer auf Deutsch
- Halte Antworten unter 200 Wörtern`,
  },
  {
    key: 'explanation_prompt',
    label: 'Erklärungsmodus',
    description: 'Prompt für detaillierte Erklärungen einzelner Fragen.',
    prompt: `Erkläre die folgende Theoriefrage ausführlich:

{question}

Gehe dabei ein auf:
1. Warum die richtige Antwort korrekt ist
2. Warum die falschen Antworten falsch sind
3. Den relevanten Paragraphen oder die Verkehrsregel
4. Ein praktisches Beispiel aus dem Alltag`,
    defaultPrompt: `Erkläre die folgende Theoriefrage ausführlich:

{question}

Gehe dabei ein auf:
1. Warum die richtige Antwort korrekt ist
2. Warum die falschen Antworten falsch sind
3. Den relevanten Paragraphen oder die Verkehrsregel
4. Ein praktisches Beispiel aus dem Alltag`,
  },
  {
    key: 'motivation_prompt',
    label: 'Motivationsmodus',
    description: 'Prompt für motivierende Nachrichten bei Lernpausen.',
    prompt: `Der Nutzer {name} hat eine Lernpause von {days} Tagen.
Sein aktueller Streak war {streak} Tage.
Er hat bisher {completed} von {total} Fragen beantwortet.

Schreibe eine kurze, motivierende Nachricht (max 3 Sätze) die:
- Verständnis zeigt
- An bisherige Erfolge erinnert
- Zum Weitermachen einlädt`,
    defaultPrompt: `Der Nutzer {name} hat eine Lernpause von {days} Tagen.
Sein aktueller Streak war {streak} Tage.
Er hat bisher {completed} von {total} Fragen beantwortet.

Schreibe eine kurze, motivierende Nachricht (max 3 Sätze) die:
- Verständnis zeigt
- An bisherige Erfolge erinnert
- Zum Weitermachen einlädt`,
  },
  {
    key: 'image_generation_prompt',
    label: 'Bildgenerierung Basis-Prompt',
    description: 'Basis-Anweisung für die KI-Bildgenerierung von Verkehrssituationen.',
    prompt: `Erstelle ein realistisches Bild einer deutschen Verkehrssituation.
Stil: Fotorealistisch, Tageslicht, klare Sicht.
Perspektive: Fahrerperspektive aus einem PKW.
Wichtig: Deutsche Verkehrszeichen, Rechtsverkehr, deutsche Straßenbeschriftung.

Spezifische Szene: {scene_description}`,
    defaultPrompt: `Erstelle ein realistisches Bild einer deutschen Verkehrssituation.
Stil: Fotorealistisch, Tageslicht, klare Sicht.
Perspektive: Fahrerperspektive aus einem PKW.
Wichtig: Deutsche Verkehrszeichen, Rechtsverkehr, deutsche Straßenbeschriftung.

Spezifische Szene: {scene_description}`,
  },
  {
    key: 'question_generation_prompt',
    label: 'Fragengenerierung',
    description:
      'System-Prompt für die KI-Generierung von Theoriefragen. Wird im Fragenmanagement unter "KI Generieren" verwendet.',
    prompt: `Du bist ein erfahrener Fahrlehrer und Prüfungsexperte für die deutsche Führerschein-Theorieprüfung (Klasse B). Erstelle prüfungsrelevante Theoriefragen.

WICHTIGE REGELN:
1. Jede Frage muss exakt 4 Antwortoptionen haben (A, B, C, D)
2. Bei "Single Choice": genau 1 korrekte Antwort
3. Bei "Multiple Choice": 2-3 korrekte Antworten
4. Jede Frage hat eine ausführliche Erklärung mit Paragraphenverweis (StVO, StVZO, FeV)
5. Fehlerpunkte: Leicht=2, Mittel=3, Schwer=5
6. Sprache: Klares, sachliches Deutsch — so wie in der echten Prüfung
7. Falsche Antworten müssen plausibel und lehrreich sein (keine offensichtlich falschen Optionen)
8. Beziehe dich auf echte Verkehrsregeln und reale Verkehrssituationen
9. Vermeide doppelte oder sehr ähnliche Fragen
10. Nutze konkrete Zahlenangaben (Geschwindigkeiten, Abstände, Promillegrenzen) wo möglich

QUALITÄTSKRITERIEN:
- Fragen sollen dem Niveau der theoretischen Führerscheinprüfung (Klasse B) entsprechen
- Situationsbeschreibungen müssen eindeutig und verständlich sein
- Jede Erklärung muss die Rechtsgrundlage nennen (z.B. § 3 Abs. 3 Nr. 1 StVO)
- Visual Prompts sollen realistische Szenen beschreiben (Kreuzung, Autobahn, Wohngebiet etc.)

KAPITELZUORDNUNG:
1: Gefahrenlehre | 2: Verhalten im Straßenverkehr | 3: Vorfahrt/Vorrang
4: Verkehrszeichen | 5: Umweltschutz | 6: Vorschriften StVO
7: Technik | 8: Eignung u. Befähigung | 9: Straßenverkehrssystem
10: Bahnübergänge | 11: Autobahn | 12: Überholen
13: Geschwindigkeit | 14: Abbiegen/Wenden

Erstelle {quantity} Fragen für Kapitel "{chapter}" mit Schwierigkeit "{difficulty}" vom Typ "{type}".

{additional_instructions}

Antworte NUR mit einem JSON-Array:
[{"question":"...","options":[{"id":"A","text":"..."},{"id":"B","text":"..."},{"id":"C","text":"..."},{"id":"D","text":"..."}],"correct":["A"],"explanation":"...","visual_prompt":"PFLICHT! Detaillierter Bildprompt auf ENGLISCH: z.B. 'Photorealistic dashboard cam POV, German urban road, a round red-bordered sign with black number 30 (StVO 274-30) clearly visible on right side of road, right-hand traffic, natural daylight.' Bei Schildern IMMER StVO-Nummer + exakte Form/Farbe/Symbol beschreiben."}]`,
    defaultPrompt: `Du bist ein erfahrener Fahrlehrer und Prüfungsexperte für die deutsche Führerschein-Theorieprüfung (Klasse B). Erstelle prüfungsrelevante Theoriefragen.

WICHTIGE REGELN:
1. Jede Frage muss exakt 4 Antwortoptionen haben (A, B, C, D)
2. Bei "Single Choice": genau 1 korrekte Antwort
3. Bei "Multiple Choice": 2-3 korrekte Antworten
4. Jede Frage hat eine ausführliche Erklärung mit Paragraphenverweis (StVO, StVZO, FeV)
5. Fehlerpunkte: Leicht=2, Mittel=3, Schwer=5
6. Sprache: Klares, sachliches Deutsch — so wie in der echten Prüfung
7. Falsche Antworten müssen plausibel und lehrreich sein (keine offensichtlich falschen Optionen)
8. Beziehe dich auf echte Verkehrsregeln und reale Verkehrssituationen
9. Vermeide doppelte oder sehr ähnliche Fragen
10. Nutze konkrete Zahlenangaben (Geschwindigkeiten, Abstände, Promillegrenzen) wo möglich

QUALITÄTSKRITERIEN:
- Fragen sollen dem Niveau der theoretischen Führerscheinprüfung (Klasse B) entsprechen
- Situationsbeschreibungen müssen eindeutig und verständlich sein
- Jede Erklärung muss die Rechtsgrundlage nennen (z.B. § 3 Abs. 3 Nr. 1 StVO)
- Visual Prompts sollen realistische Szenen beschreiben (Kreuzung, Autobahn, Wohngebiet etc.)

KAPITELZUORDNUNG:
1: Gefahrenlehre | 2: Verhalten im Straßenverkehr | 3: Vorfahrt/Vorrang
4: Verkehrszeichen | 5: Umweltschutz | 6: Vorschriften StVO
7: Technik | 8: Eignung u. Befähigung | 9: Straßenverkehrssystem
10: Bahnübergänge | 11: Autobahn | 12: Überholen
13: Geschwindigkeit | 14: Abbiegen/Wenden

Erstelle {quantity} Fragen für Kapitel "{chapter}" mit Schwierigkeit "{difficulty}" vom Typ "{type}".

{additional_instructions}

Antworte NUR mit einem JSON-Array:
[{"question":"...","options":[{"id":"A","text":"..."},{"id":"B","text":"..."},{"id":"C","text":"..."},{"id":"D","text":"..."}],"correct":["A"],"explanation":"...","visual_prompt":"PFLICHT! Detaillierter Bildprompt auf ENGLISCH: z.B. 'Photorealistic dashboard cam POV, German urban road, a round red-bordered sign with black number 30 (StVO 274-30) clearly visible on right side of road, right-hand traffic, natural daylight.' Bei Schildern IMMER StVO-Nummer + exakte Form/Farbe/Symbol beschreiben."}]`,
  },
];

export const AiPromptsPage: React.FC = () => {
  const [prompts, setPrompts] = useState<PromptTemplate[]>(DEFAULT_PROMPTS);
  const [saving, setSaving] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const handleChange = (key: string, value: string) => {
    setPrompts((prev) => prev.map((p) => (p.key === key ? { ...p, prompt: value } : p)));
  };

  const handleSave = (key: string) => {
    setSaving(key);
    // Simulate save — in production, store in Supabase
    setTimeout(() => {
      setSaving(null);
      setSaved(key);
      setTimeout(() => setSaved(null), 2000);
    }, 500);
  };

  const handleReset = (key: string) => {
    if (!confirm('Prompt auf Standard zurücksetzen?')) return;
    setPrompts((prev) => prev.map((p) => (p.key === key ? { ...p, prompt: p.defaultPrompt } : p)));
  };

  return (
    <div>
      <div className="page-header">
        <h1>
          <MessageSquare size={24} /> KI Prompts
        </h1>
      </div>

      <div className="alert alert-info mb-6">
        <MessageSquare size={16} />
        <span>
          Hier kannst du die Systemprompts für den KI-Coach und die Bildgenerierung anpassen.
          Änderungen werden sofort für neue Anfragen wirksam.
        </span>
      </div>

      <div className="flex flex-col gap-6">
        {prompts.map((p) => (
          <div key={p.key} className="card">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3>{p.label}</h3>
                <p className="text-sm text-muted">{p.description}</p>
              </div>
              <span className="text-mono text-xs text-muted">{p.key}</span>
            </div>
            <textarea
              className="input"
              rows={8}
              value={p.prompt}
              onChange={(e) => handleChange(p.key, e.target.value)}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.6 }}
            />
            <div className="flex justify-between mt-3">
              <button className="btn btn-secondary btn-sm" onClick={() => handleReset(p.key)}>
                <RotateCcw size={14} /> Zurücksetzen
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleSave(p.key)}
                disabled={saving === p.key}
              >
                {saving === p.key ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : saved === p.key ? (
                  <CheckCircle size={14} />
                ) : (
                  <Save size={14} />
                )}
                {saved === p.key ? 'Gespeichert!' : 'Speichern'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
